/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    initialized: false,
    contractor: {
        id: null,
        statusId: '',
        name: '',
        termNet: '',
        files: [],
        qualifications: [],
        financialSyncStatus: null,
        financialSyncTime: null,
        teamMembers: [],
        detailsEdit: false,
        paymentEdit: false,
        tradesEdit: false,
        coverageEdit: false,
        teamMemberEdit: false,
        teamMemberAdd: false,
        genericTeamMemberAdd: false,
        teamMemberActivate: false,
        teamMemberDeactivate: false,
        editingMemberId: null,
    },
    statuses: [],
    paymentMethods: [],
    corpTypes: [],
    updated: false,
};

export const createCompanyDetailsSlice = sliceName => {
    const companyDetailsSlice = createSlice({
        name: sliceName,
        initialState,
        reducers: {
            /**
             * Initializes the contractor store with data
             * @param {Object} action.payload - Initial data
             * @param {Object} action.payload.contractor - Contractor data
             * @param {Array} action.payload.statuses - Status options
             * @param {Array} action.payload.paymentMethods - Payment method options
             * @param {Array} action.payload.corpTypes - Corporation type options
             */
            init: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    contractor: action.payload.contractor,
                    statuses: action.payload.statuses,
                    paymentMethods: action.payload.paymentMethods,
                    corpTypes: action.payload.corpTypes,
                };
            },

            /**
             * Replaces the contractor data
             * @param {Object} action.payload - New contractor data
             */
            replace: (state, action) => {
                return {
                    ...state,
                    contractor: action.payload,
                    updated: true,
                };
            },

            /**
             * Adds a note to the contractor
             * @param {Object} action.payload - Note object
             * @param {number} action.payload.id
             * @param {number} action.payload.accountId
             * @param {number} action.payload.contractorId
             * @param {string} action.payload.created
             * @param {string} action.payload.fullName
             * @param {string} action.payload.message
             * @param {string} action.payload.messageType
             * @param {string} action.payload.updated
             */
            addNote: (state, action) => {
                const notes = state.contractor.notes || [];
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        notes: [...notes, action.payload],
                    },
                };
            },

            /**
             * Adds a rating to the contractor
             * @param {Object} action.payload - Rating object
             */
            addRating: (state, action) => {
                const ratings = state.contractor.ratings || [];
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        ratings: [...ratings, action.payload],
                    },
                };
            },

            /**
             * Updates an existing note
             * @param {Object} action.payload - Updated note object
             * @param {number} action.payload.id
             * @param {number} action.payload.accountId
             * @param {number} action.payload.contractorId
             * @param {string} action.payload.created
             * @param {string} action.payload.fullName
             * @param {string} action.payload.message
             * @param {string} action.payload.messageType
             * @param {string} action.payload.updated
             */
            updateNote: (state, action) => {
                const updatedNotes = state.contractor.notes.map(note =>
                    note.id === action.payload.id ? action.payload : note
                );
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        notes: updatedNotes,
                    },
                };
            },

            /**
             * Adds a file to contractor
             * @param {Object} action.payload - File object
             */
            addFile: (state, action) => {
                const files = state.contractor.files || [];
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        files: [...files, action.payload],
                    },
                };
            },

            /**
             * Removes a file from contractor
             * @param {string} action.payload - File ID
             */
            removeFile: (state, action) => {
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        files: state.contractor.files.filter(
                            file => file.id !== action.payload
                        ),
                    },
                };
            },

            /**
             * Adds a qualification to contractor
             * @param {Object} action.payload - Qualification object
             */
            addQualification: (state, action) => {
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        qualifications: [
                            ...state.contractor.qualifications,
                            action.payload,
                        ],
                    },
                };
            },

            /**
             * Removes a qualification from contractor
             * @param {string} action.payload - Qualification ID
             */
            removeQualification: (state, action) => {
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        qualifications: state.contractor.qualifications.filter(
                            q => q.id !== action.payload
                        ),
                    },
                };
            },

            /**
             * Adds an item to the contractor blocklist
             * @param {Object} action.payload - Blocklist item
             */
            addBlocklistItem: (state, action) => {
                const blacklists = state.contractor.blacklists || [];
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        blacklists: [...blacklists, action.payload],
                    },
                };
            },

            /**
             * Removes an item from the contractor blocklist
             * @param {string} action.payload - Blocklist item ID
             */
            removeBlocklistItem: (state, action) => {
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        blacklists: state.contractor.blacklists.filter(
                            b => b.id !== action.payload
                        ),
                    },
                };
            },

            /**
             * Replaces an item in the contractor blocklist
             * @param {Object} action.payload - Replacement blocklist item
             */
            replaceBlocklistItem: (state, action) => {
                return {
                    ...state,
                    contractor: {
                        ...state.contractor,
                        blacklists: state.contractor.blacklists.map(b =>
                            b.id === action.payload.id ? action.payload : b
                        ),
                    },
                };
            },
        },
    });

    return {
        reducer: companyDetailsSlice.reducer,
        actions: companyDetailsSlice.actions,
    };
};
